import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSave,
  FaTimes,
  FaSpinner,
  FaGlobe,
  FaCheck,
  FaBan,
  FaCalendarAlt,
  FaLink,
} from "react-icons/fa";
import axios from "axios";
import toast from "react-hot-toast";

const RecentResultsManager = () => {
  const [recentResults, setRecentResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingResult, setEditingResult] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    examType: "Regular",
    url: "",
  });

  useEffect(() => {
    checkDarkMode();
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const checkMobile = () => {
    setIsMobile(window.innerWidth <= 768);
  };

  const checkDarkMode = () => {
    const dark = document.documentElement.getAttribute("data-theme") === "dark";
    setIsDarkMode(dark);
    const observer = new MutationObserver(() => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  };

  useEffect(() => {
    fetchRecentResults();
  }, []);

  const fetchRecentResults = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        "/api/results/admin/recent",
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setRecentResults(response.data);
    } catch (error) {
      console.error("Fetch recent results error:", error);
      toast.error("Failed to fetch recent results");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.url) {
      toast.error("Please fill Name and URL");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      if (editingResult) {
        await axios.put(
          `/api/results/admin/recent/${editingResult._id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        toast.success("Recent result updated");
      } else {
        await axios.post(
          "/api/results/admin/recent",
          formData,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        toast.success("Recent result added");
      }
      fetchRecentResults();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error("Save error:", error);
      toast.error(error.response?.data?.message || "Failed to save");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `/api/results/admin/recent/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Deleted successfully");
      fetchRecentResults();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete");
    }
  };

  const handleToggleActive = async (result, isActive) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `/api/results/admin/recent/${result._id}`,
        { ...result, isActive },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(isActive ? "Activated" : "Deactivated");
      fetchRecentResults();
    } catch (error) {
      console.error("Toggle error:", error);
      toast.error("Failed to update status");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      examType: "Regular",
      url: "",
    });
    setEditingResult(null);
  };

  // Helper functions for styling
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  // Animation variants - simplified to avoid color animation errors
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 10, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.2 },
    },
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "3rem" }}>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        >
          <FaSpinner size={32} color="#8b5cf6" />
        </motion.div>
        <p style={{ marginTop: "1rem", color: getTextSecondary() }}>
          Loading recent results...
        </p>
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1.5rem",
          flexWrap: "wrap",
          gap: "1rem",
        }}
      >
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              marginBottom: "0.3rem",
            }}
          >
            <div
              style={{
                width: "32px",
                height: "32px",
                background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FaGlobe size={16} color="white" />
            </div>
            <h2
              style={{
                fontSize: "1.3rem",
                fontWeight: "bold",
                color: getTextColor(),
                margin: 0,
              }}
            >
              Recent Results Management
            </h2>
          </div>
          <p
            style={{
              color: getTextSecondary(),
              fontSize: "0.8rem",
              marginLeft: "2.8rem",
            }}
          >
            Manage exam periods that appear in the student dropdown for quick
            result access
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          style={{
            padding: "0.6rem 1.2rem",
            background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
            color: "white",
            border: "none",
            borderRadius: "12px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            fontSize: "0.85rem",
            fontWeight: "600",
            boxShadow: "0 4px 14px 0 rgba(139,92,246,0.4)",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.transform = "scale(1.02)")
          }
          onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
        >
          <FaPlus /> Add New Exam Period
        </button>
      </div>

      {/* Results Table */}
      {recentResults.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "3rem",
            background: getCardBackground(),
            borderRadius: "20px",
            border: `1px solid ${getBorderColor()}`,
          }}
        >
          <div
            style={{
              width: "70px",
              height: "70px",
              background: isDarkMode
                ? "rgba(139,92,246,0.1)"
                : "rgba(139,92,246,0.05)",
              borderRadius: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto",
            }}
          >
            <FaGlobe size={35} color="#8b5cf6" />
          </div>
          <p style={{ marginTop: "1rem", color: getTextColor() }}>
            No recent results configured
          </p>
          <p style={{ color: getTextSecondary(), fontSize: "0.8rem" }}>
            Click "Add New Exam Period" to get started
          </p>
        </div>
      ) : (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          style={{
            background: getCardBackground(),
            borderRadius: "20px",
            overflow: "hidden",
            border: `1px solid ${getBorderColor()}`,
          }}
        >
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr
                  style={{
                    background: isDarkMode
                      ? "rgba(139,92,246,0.05)"
                      : "#f8f9fa",
                    borderBottom: `2px solid ${getBorderColor()}`,
                  }}
                >
                  <th
                    style={{
                      padding: "1rem",
                      textAlign: "left",
                      color: getTextColor(),
                    }}
                  >
                    Exam Period
                  </th>
                  <th
                    style={{
                      padding: "1rem",
                      textAlign: "center",
                      color: getTextColor(),
                    }}
                  >
                    Exam Type
                  </th>
                  <th
                    style={{
                      padding: "1rem",
                      textAlign: "center",
                      color: getTextColor(),
                    }}
                  >
                    URL
                  </th>
                  <th
                    style={{
                      padding: "1rem",
                      textAlign: "center",
                      color: getTextColor(),
                    }}
                  >
                    Status
                  </th>
                  <th
                    style={{
                      padding: "1rem",
                      textAlign: "center",
                      color: getTextColor(),
                    }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {recentResults.map((result, idx) => (
                  <motion.tr
                    key={result._id}
                    variants={itemVariants}
                    style={{
                      borderBottom: `1px solid ${getBorderColor()}`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = isDarkMode
                        ? "rgba(139,92,246,0.05)"
                        : "rgba(139,92,246,0.03)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <td style={{ padding: "1rem" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        <div
                          style={{
                            width: "28px",
                            height: "28px",
                            background: "rgba(139,92,246,0.1)",
                            borderRadius: "8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <FaCalendarAlt size={12} color="#8b5cf6" />
                        </div>
                        <div
                          style={{ fontWeight: "500", color: getTextColor() }}
                        >
                          {result.name}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: "1rem", textAlign: "center" }}>
                      <span
                        style={{
                          background:
                            result.examType === "Regular"
                              ? "rgba(16,185,129,0.1)"
                              : "rgba(245,158,11,0.1)",
                          color:
                            result.examType === "Regular"
                              ? "#10b981"
                              : "#f59e0b",
                          padding: "0.2rem 0.6rem",
                          borderRadius: "20px",
                          fontSize: "0.7rem",
                          fontWeight: "500",
                        }}
                      >
                        {result.examType}
                      </span>
                    </td>
                    <td style={{ padding: "1rem", textAlign: "center" }}>
                      <a
                        href={result.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#8b5cf6",
                          textDecoration: "none",
                          fontSize: "0.7rem",
                          wordBreak: "break-all",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "0.3rem",
                        }}
                        onMouseEnter={(e) =>
                          (e.target.style.textDecoration = "underline")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.textDecoration = "none")
                        }
                      >
                        <FaLink size={10} /> {result.url?.substring(0, 40)}...
                      </a>
                    </td>
                    <td style={{ padding: "1rem", textAlign: "center" }}>
                      <span
                        style={{
                          background: result.isActive
                            ? "rgba(16,185,129,0.1)"
                            : "rgba(239,68,68,0.1)",
                          color: result.isActive ? "#10b981" : "#ef4444",
                          padding: "0.2rem 0.6rem",
                          borderRadius: "20px",
                          fontSize: "0.7rem",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "0.3rem",
                        }}
                      >
                        {result.isActive ? (
                          <FaCheck size={10} />
                        ) : (
                          <FaBan size={10} />
                        )}
                        {result.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td style={{ padding: "1rem", textAlign: "center" }}>
                      <div
                        style={{
                          display: "flex",
                          gap: "0.3rem",
                          justifyContent: "center",
                        }}
                      >
                        <button
                          onClick={() =>
                            handleToggleActive(result, !result.isActive)
                          }
                          style={{
                            padding: "0.3rem 0.6rem",
                            background: result.isActive
                              ? "linear-gradient(135deg, #ef4444, #dc2626)"
                              : "linear-gradient(135deg, #10b981, #059669)",
                            color: "white",
                            border: "none",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "0.7rem",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.3rem",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.opacity = "0.9")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.opacity = "1")
                          }
                        >
                          {result.isActive ? "Deactivate" : "Activate"}
                        </button>
                        <button
                          onClick={() => {
                            setEditingResult(result);
                            setFormData({
                              name: result.name,
                              examType: result.examType,
                              url: result.url,
                            });
                            setShowModal(true);
                          }}
                          style={{
                            padding: "0.3rem 0.6rem",
                            background:
                              "linear-gradient(135deg, #3b82f6, #1d4ed8)",
                            color: "white",
                            border: "none",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "0.7rem",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.3rem",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.opacity = "0.9")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.opacity = "1")
                          }
                        >
                          <FaEdit size={10} /> Edit
                        </button>
                        <button
                          onClick={() => handleDelete(result._id)}
                          style={{
                            padding: "0.3rem 0.6rem",
                            background:
                              "linear-gradient(135deg, #ef4444, #dc2626)",
                            color: "white",
                            border: "none",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "0.7rem",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.3rem",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.opacity = "0.9")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.opacity = "1")
                          }
                        >
                          <FaTrash size={10} /> Delete
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.5)",
              backdropFilter: "blur(4px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: "1rem",
            }}
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 30 }}
              transition={{ type: "spring", damping: 25 }}
              style={{
                background: getCardBackground(),
                backdropFilter: "blur(20px)",
                borderRadius: "24px",
                maxWidth: "450px",
                width: "100%",
                padding: "1.5rem",
                border: `1px solid ${getBorderColor()}`,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1rem",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {editingResult ? (
                      <FaEdit size={14} color="white" />
                    ) : (
                      <FaPlus size={14} color="white" />
                    )}
                  </div>
                  <h3
                    style={{
                      fontSize: "1.2rem",
                      fontWeight: "bold",
                      color: getTextColor(),
                    }}
                  >
                    {editingResult ? "Edit Exam Period" : "Add New Exam Period"}
                  </h3>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    background: isDarkMode
                      ? "rgba(139,92,246,0.1)"
                      : "rgba(139,92,246,0.05)",
                    border: "none",
                    width: "28px",
                    height: "28px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    color: getTextSecondary(),
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = isDarkMode
                      ? "rgba(139,92,246,0.2)"
                      : "rgba(139,92,246,0.1)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = isDarkMode
                      ? "rgba(139,92,246,0.1)"
                      : "rgba(139,92,246,0.05)")
                  }
                >
                  <FaTimes />
                </button>
              </div>

              {/* Name */}
              <div style={{ marginBottom: "1rem" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.3rem",
                    color: getTextSecondary(),
                    fontSize: "0.8rem",
                    fontWeight: "500",
                  }}
                >
                  Display Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="e.g., December 2025 Exams"
                  style={{
                    width: "100%",
                    padding: "0.6rem",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "10px",
                    fontSize: "0.85rem",
                    outline: "none",
                    background: getInputBackground(),
                    color: getTextColor(),
                    transition: "all 0.3s",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                  onBlur={(e) =>
                    (e.target.style.borderColor = getBorderColor())
                  }
                />
              </div>

              {/* Exam Type */}
              <div style={{ marginBottom: "1rem" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.3rem",
                    color: getTextSecondary(),
                    fontSize: "0.8rem",
                    fontWeight: "500",
                  }}
                >
                  Exam Type
                </label>
                <select
                  value={formData.examType}
                  onChange={(e) =>
                    setFormData({ ...formData, examType: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "0.6rem",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "10px",
                    fontSize: "0.85rem",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                >
                  <option value="Regular">Regular</option>
                  <option value="Revaluation">Revaluation</option>
                </select>
              </div>

              {/* URL */}
              <div style={{ marginBottom: "1rem" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.3rem",
                    color: getTextSecondary(),
                    fontSize: "0.8rem",
                    fontWeight: "500",
                  }}
                >
                  Result URL *
                </label>
                <input
                  type="text"
                  value={formData.url}
                  onChange={(e) =>
                    setFormData({ ...formData, url: e.target.value })
                  }
                  placeholder="https://results.vtu.ac.in/..."
                  style={{
                    width: "100%",
                    padding: "0.6rem",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "10px",
                    fontSize: "0.85rem",
                    outline: "none",
                    background: getInputBackground(),
                    color: getTextColor(),
                    transition: "all 0.3s",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                  onBlur={(e) =>
                    (e.target.style.borderColor = getBorderColor())
                  }
                />
              </div>

              {/* Buttons */}
              <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    flex: 1,
                    padding: "0.6rem",
                    background: isDarkMode
                      ? "rgba(100,116,139,0.3)"
                      : "#e2e8f0",
                    color: getTextColor(),
                    border: "none",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontSize: "0.85rem",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.8")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  style={{
                    flex: 1,
                    padding: "0.6rem",
                    background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    color: "white",
                    border: "none",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontSize: "0.85rem",
                    fontWeight: "600",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                >
                  <FaSave style={{ marginRight: "0.3rem" }} />{" "}
                  {editingResult ? "Update" : "Add"}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default RecentResultsManager;

